// Ported from https://github.com/hautaiou/saas-ml-algorithms/blob/dev/hautai/saas/algorithms/face/detector/utils.py

import { createCanvas } from "@/libs/canvas"
import { getImageSourceDimensions, imageBitmapToPixels, type ImageSource } from "@/libs/image"
import { isAndroid } from "@/libs/software"
import type { FaceCropperBoundingBox, FaceDetection, FramePixels, Landmarks } from "../types"

export function findCenterOfMass(
  points: Landmarks,
  imageShape: [number, number],
): [number, number] {
  const [height, width] = imageShape
  const numPoints = points.length
  const sumX = points.reduce((acc, point) => acc + point.x * width, 0)
  const sumY = points.reduce((acc, point) => acc + point.y * height, 0)
  const centerX = sumX / numPoints
  const centerY = sumY / numPoints
  return [centerX, centerY]
}

export function imgShapeToBbox(imageShape: [number, number]): [number, number][] {
  const [height, width] = imageShape
  return [
    [0, 0],
    [width, 0],
    [width, height],
    [0, height],
  ]
}

export function findSafePaddings(
  eyeAngleDegrees: number,
  imageHeight: number,
  imageWidth: number,
): [number, number] {
  const bbox = imgShapeToBbox([imageHeight, imageWidth])
  const inputCenter = [imageWidth / 2.0, imageHeight / 2.0]
  const angle = (eyeAngleDegrees * Math.PI) / 180
  const cosAngle = Math.cos(angle)
  const sinAngle = Math.sin(angle)
  const matrix = [
    [cosAngle, -sinAngle],
    [sinAngle, cosAngle],
  ]

  const transformedBbox = bbox.map(([x, y]) => {
    const xShifted = x - inputCenter[0]
    const yShifted = y - inputCenter[1]
    return [
      matrix[0][0] * xShifted + matrix[0][1] * yShifted,
      matrix[1][0] * xShifted + matrix[1][1] * yShifted,
    ]
  })

  const xs = transformedBbox.map(([x]) => x)
  const ys = transformedBbox.map(([, y]) => y)
  const wNew = Math.ceil(Math.max(...xs)) - Math.floor(Math.min(...xs))
  const hNew = Math.ceil(Math.max(...ys)) - Math.floor(Math.min(...ys))

  const paddingX = Math.max(0, Math.floor((wNew - imageWidth) / 2))
  const paddingY = Math.max(0, Math.floor((hNew - imageHeight) / 2))
  return [paddingY, paddingX]
}

export function calculateBoxXShift(
  boundingBox: number[],
  facialMesh: Landmarks,
  centerOfMass: [number, number] | null = null,
): number {
  const [x1, _, x2, __] = boundingBox
  const boxWidth = x2 - x1

  const boxCenterX = boxWidth / 2
  if (!centerOfMass) {
    centerOfMass = findCenterOfMass(facialMesh, [1, 1])
  }
  const [faceCenterX, ___] = centerOfMass
  return boxCenterX - faceCenterX
}

export function findAngleBetweenEyes(
  facialMesh: Landmarks,
  imageHeight: number,
  imageWidth: number,
): number {
  const imageShape = [imageHeight, imageWidth] as [number, number]

  const leftEyeIndices = [33, 7, 163, 144, 145, 153, 154, 155]
  const rightEyeIndices = [263, 249, 390, 373, 374, 380, 381, 382]

  const leftEyePoints = leftEyeIndices.map((index) => facialMesh[index])
  const rightEyePoints = rightEyeIndices.map((index) => facialMesh[index])

  const leftEyeCenter = findCenterOfMass(leftEyePoints, imageShape)
  const rightEyeCenter = findCenterOfMass(rightEyePoints, imageShape)

  const xLeg = rightEyeCenter[0] - leftEyeCenter[0]
  const yLeg = rightEyeCenter[1] - leftEyeCenter[1]

  const angleRadians = Math.atan2(yLeg, xLeg)
  return angleRadians
}

export const rotateImage = (canvas: OffscreenCanvas, angleRadians: number): OffscreenCanvas => {
  const { width, height } = canvas

  const rotatedCanvas = new OffscreenCanvas(width, height)
  const ctx = rotatedCanvas.getContext("2d", { alpha: true })!

  ctx.fillStyle = "black"
  ctx.fillRect(0, 0, width, height)

  ctx.translate(width / 2, height / 2)
  ctx.rotate(-angleRadians)
  ctx.drawImage(canvas, -width / 2, -height / 2)

  return rotatedCanvas
}

export function multiplyMatrixAndPoint(matrix: number[][], point: number[]): number[] {
  const [m0, m1, m2] = matrix
  const [x, y, z] = point
  const xNew = m0[0] * x + m0[1] * y + m0[2] * z
  const yNew = m1[0] * x + m1[1] * y + m1[2] * z
  const zNew = m2[0] * x + m2[1] * y + m2[2] * z
  return [xNew, yNew, zNew]
}

const FIX_FOREHEAD_POINTS: Array<[number[], number]> = [
  [[67, 109, 10, 338, 297], 1.0],
  [[103, 332, 104, 69, 108, 151, 337, 299, 333], 0.5],
  [[67, 109, 10, 338, 297], 0.0],
]

export function fixForeheadPoints(landmarks: Landmarks): Landmarks {
  const adjustedLandmarks = landmarks.map((point) => ({ ...point }))

  const height = landmarks[151].y - landmarks[10].y

  FIX_FOREHEAD_POINTS.forEach(([pointIndices, adjustCoeff]) => {
    const offset = height * adjustCoeff
    pointIndices.forEach((index) => {
      if (index < adjustedLandmarks.length) {
        adjustedLandmarks[index].y -= offset
      }
    })
  })

  return adjustedLandmarks
}

export const calculateFaceBoundingBox = (
  faceLandmarks: Landmarks,
  width: number,
  height: number,
): FaceCropperBoundingBox => {
  const adjustedLandmarks = fixForeheadPoints(faceLandmarks)

  let minX = Infinity,
    minY = Infinity
  let maxX = -Infinity,
    maxY = -Infinity

  adjustedLandmarks.forEach((landmark) => {
    const x = landmark.x * width
    const y = landmark.y * height

    if (x < minX) minX = x
    if (y < minY) minY = y
    if (x > maxX) maxX = x
    if (y > maxY) maxY = y
  })

  const bboxWidth = maxX - minX
  const bboxHeight = maxY - minY

  const squareSize = Math.max(bboxWidth, bboxHeight)

  const centerX = (minX + maxX) / 2
  const centerY = (minY + maxY) / 2

  return { minX, minY, maxX, maxY, centerX, centerY, squareSize }
}

/**
 * Mobile devices, especially on Chrome for Android, often have more limited GPU/CPU resources than desktops.
 * High-resolution images can cause the face detection algorithms (or the underlying WebGL/image processing) to fail.
 *
 * Total Peak Memory (Approximate):
 * On Android devices (especially lower‑end ones), using a 1024px image might require roughly
 * 4 MB (raw image) + 8 MB (resized array) + 2 MB (grayscale) + 4 MB (FFT data) ≈ 18 MB.
 * In practice, this is often considered near the upper limit of what’s “safe” on many Android devices.
 */
const SAFE_IMAGE_SIZE = isAndroid ? 1024 : 2024

export const extractFacePixels = async (
  source: ImageSource,
  faceDetection: FaceDetection | null,
): Promise<FramePixels> => {
  if (!faceDetection?.boundingBox) {
    const fullBitmap = await createImageBitmap(source)
    return imageBitmapToPixels(fullBitmap)
  }

  const { x, y, width, height } = faceDetection.boundingBox
  const { width: srcW, height: srcH } = getImageSourceDimensions(source)

  const px = Math.floor(x * srcW)
  const py = Math.floor(y * srcH)
  const pw = Math.floor(width * srcW)
  const ph = Math.floor(height * srcH)

  // SHRINKING: factor <1.0 to crop closer to center
  const shrinkFactor = 0.8
  const dx = Math.floor((pw * (1 - shrinkFactor)) / 2)
  const dy = Math.floor((ph * (1 - shrinkFactor)) / 2)

  const cropX = Math.max(0, px + dx)
  const cropY = Math.max(0, py + dy)
  const cropW = Math.floor(pw * shrinkFactor)
  const cropH = Math.floor(ph * shrinkFactor)

  const finalCropW = Math.min(cropW, srcW - cropX)
  const finalCropH = Math.min(cropH, srcH - cropY)

  const faceBitmap = await createImageBitmap(source, cropX, cropY, finalCropW, finalCropH)

  let tw = faceBitmap.width
  let th = faceBitmap.height
  if (tw > SAFE_IMAGE_SIZE || th > SAFE_IMAGE_SIZE) {
    const scale = Math.min(SAFE_IMAGE_SIZE / tw, SAFE_IMAGE_SIZE / th)
    tw = Math.floor(tw * scale)
    th = Math.floor(th * scale)
  }

  const canvas = createCanvas(tw, th)
  const ctx = canvas.getContext("2d")! as CanvasRenderingContext2D
  ctx.drawImage(faceBitmap, 0, 0, tw, th)
  const imageData = ctx.getImageData(0, 0, tw, th)

  return {
    data: new Uint8Array(imageData.data),
    width: tw,
    height: th,
    format: "RGBA",
  }
}
